import React from 'react';
import { Routes, Route, useParams } from 'react-router-dom'; // useParams importieren
import { ColorModeProvider } from './context/ColorModeContext';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import { AuthProvider } from './context/AuthProvider';
import HomePage from './pages/HomePage';
import { NotificationProvider } from './context/NotificationContext';
import ResultsPage from './pages/ResultsPage';
import HistoryPage from './pages/HistoryPage';
import ContactPage from './pages/ContactPage';
import TeamsPage from './pages/TeamsPage';
import ImpressumPage from './pages/ImpressumPage';
import PrivacyPage from './pages/PrivacyPage';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/comfortaa';
import VenuesPage from './pages/VenuesPage';
import RulesPage from './pages/RulesPage';
import BookingOverview from './components/BookingOverview';
import LoginPage from './pages/LoginPage';
import AdminBoard from './pages/AdminBoard';
import AdminDashboard from './components/Admin/AdminDashboard';
import DashboardPage from './pages/DashboardPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TeamPage from './pages/TeamPage';
import TeamDetailPage from './pages/TeamDetailPage';
import { checkBackendHealth } from './services/apiClient';
import BackendErrorPage from './pages/BackendErrorPage';


// Eine kleine Helfer-Komponente, um den URL-Parameter an das AdminBoard zu übergeben
function AdminBoardWrapper() {
  // Holt den 'tab'-Teil aus der URL (z.B. 'bookings', 'users')
  const { tab } = useParams();
  // Übergibt den Tab an das AdminBoard. Wenn kein Tab da ist, wird 'bookings' als Standard verwendet.
  return <AdminBoard initialTab={tab || 'bookings'} />;
}

function App() {
  const [isBackendLive, setIsBackendLive] = React.useState(null); // null = checking, true = live, false = dead

  React.useEffect(() => {
    const checkStatus = async () => {
      const isLive = await checkBackendHealth();
      setIsBackendLive(isLive);
    };
    checkStatus();
  }, []);

  if (isBackendLive === false) {
    return (
      <ColorModeProvider>
        <CssBaseline />
        <BackendErrorPage />
      </ColorModeProvider>
    );
  }

  // Solange wir prüfen (isBackendLive === null), könnten wir einen Ladescreen zeigen
  // oder einfach die App rendern, die dann ggf. kurz "hakt" oder Loading zeigt.
  // Hier rendern wir die App erst, wenn wir wissen, dass es geht. 
  // ODER: Wir rendern die App sofort und zeigen den Error nur bei explizitem False.
  // Entscheidung: Wir warten kurz (Ladescreen) oder zeigen App, und wenn Check fehlschlägt -> Error Page.
  // Da der App-Start u.U. Requests feuert, ist warten sicherer um Errors zu vermeiden.
  // Aber für "Fast Load" Gefühl: App rendern. Wenn Check fehlschlägt, wird sie durch ErrorPage ersetzt.
  // Kompromiss: Wenn isBackendLive === false -> ErrorPage. Sonst App.
  // (Bei 'null' wird die App schon gerendert. Falls Request failen, fängt User das ab, 
  // bis Check fertig ist und ErrorPage kommt.)

  return (
    <ColorModeProvider>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <div className="app-container">
            <Header />
            <main className="main-content">
              <Routes>
                <Route path="/tabelle" element={<ResultsPage />} />
                <Route path="/platzreservierung" element={<BookingOverview />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* --- NEUE ADMIN ROUTEN --- */}
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/:tab" element={<AdminBoardWrapper />} />
                <Route path="/admin" element={<AdminDashboard />} />

                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/historie" element={<HistoryPage />} />
                <Route path="/kontakt" element={<ContactPage />} />
                <Route path="/teams" element={<TeamsPage />} />
                <Route path="/impressum" element={<ImpressumPage />} />
                <Route path="/datenschutz" element={<PrivacyPage />} />
                <Route path="/ueberuns" element={<VenuesPage />} />
                <Route path="/regeln" element={<RulesPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/team/:teamId" element={<TeamDetailPage />} />
                {/* Die Catch-all Route muss immer am Ende stehen */}
                <Route path="*" element={<HomePage />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </NotificationProvider>
      </AuthProvider>
    </ColorModeProvider>
  );
}

export default App;
